import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { LeadTable } from "../features/leads/LeadTable";
import { LeadFilterBar } from "../features/leads/LeadFilterBar";
import { useLeadsQuery } from "../features/leads/api";
import { useLeadFilters } from "../features/leads/useLeadFilters";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { ExportLeadsModal } from "../features/exports/ExportLeadsModal";
import type { LeadFilters } from "../features/leads/useLeadFilters";
import { useAuth } from "../features/auth/AuthProvider";

const PAGE_SIZE = 20;

export default function LeadsPage() {
  const { current } = useWorkspace();
  const { user } = useAuth();
  // Sales reps default to seeing their queue ordered by most-recently-assigned
  // when they haven't picked an explicit sort.
  const isSales = !!user && !user.isSuperadmin && user.role === "sales";
  const {
    filters: urlFilters,
    activeCount,
    setStatus,
    setSource,
    setAssignee,
    setCampaign,
    setCity,
    setState,
    setSearch,
    setFrom,
    setTo,
    setFollowUp,
    setSort,
    setPage,
    clear,
  } = useLeadFilters();
  const {
    status,
    source,
    assignee,
    campaign,
    city,
    state,
    q: search,
    from,
    to,
    followUp,
    sort,
    page,
  } = urlFilters;

  // Local mirror of the search input so each keystroke gives instant feedback;
  // the debounced value is what actually lands in the URL.
  const [searchInput, setSearchInput] = useState(search);
  const lastUrlSearch = useRef(search);
  useEffect(() => {
    if (search !== lastUrlSearch.current) {
      setSearchInput(search);
      lastUrlSearch.current = search;
    }
  }, [search]);

  const debounce = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    const trimmed = searchInput.trim();
    if (trimmed === search) return;
    debounce.current = window.setTimeout(() => {
      lastUrlSearch.current = trimmed;
      setSearch(trimmed);
    }, 300);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [searchInput, search, setSearch]);

  const filters = useMemo(
    () => ({
      ...(status ? { status } : {}),
      ...(source ? { source } : {}),
      ...(assignee ? { assignedTo: assignee } : {}),
      ...(campaign ? { campaign } : {}),
      ...(city.length ? { city } : {}),
      ...(state.length ? { state } : {}),
      ...(search ? { q: search } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(followUp === "due" || followUp === "scheduled"
        ? { followUp: followUp as "due" | "scheduled" }
        : {}),
      ...(sort === "follow_up_asc"
        ? { sortBy: "follow_up" as const, sortDir: "asc" as const }
        : sort === "follow_up_desc"
        ? { sortBy: "follow_up" as const, sortDir: "desc" as const }
        : sort === "assigned_asc"
        ? { sortBy: "assigned_at" as const, sortDir: "asc" as const }
        : sort === "assigned_desc"
        ? { sortBy: "assigned_at" as const, sortDir: "desc" as const }
        : isSales
        ? { sortBy: "assigned_at" as const, sortDir: "desc" as const }
        : {}),
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [status, source, assignee, campaign, city, state, search, from, to, followUp, sort, page, isSales]
  );

  const { data, isLoading, isError, error, isFetching } = useLeadsQuery(
    current?.slug ?? null,
    filters
  );

  const leads = data?.leads ?? [];
  const total = data?.pagination.total ?? 0;
  const start = page * PAGE_SIZE;
  const end = start + leads.length;
  const hasPrev = page > 0;
  const hasNext = total > end;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleClear = () => {
    setSearchInput("");
    lastUrlSearch.current = "";
    clear();
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">
            {current ? current.name : "Pick a workspace to begin"}
          </p>
        </div>
        <ExportControls filters={urlFilters} />
      </header>

      <Card title="All leads">
        <LeadFilterBar
          workspaceSlug={current?.slug ?? null}
          filters={urlFilters}
          activeCount={activeCount}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          setStatus={setStatus}
          setSource={setSource}
          setAssignee={setAssignee}
          setCampaign={setCampaign}
          setCity={setCity}
          setState={setState}
          setFrom={setFrom}
          setTo={setTo}
          setFollowUp={setFollowUp}
          setSort={setSort}
          clear={handleClear}
        />

        {isLoading ? (
          <div className="skeleton-rows">Loading…</div>
        ) : isError ? (
          <div className="form-error">{(error as Error).message}</div>
        ) : (
          <>
            <LeadTable leads={leads} />
            <div className="pagination">
              <div className="pagination-info">
                {total === 0 ? (
                  "0 leads"
                ) : (
                  <>
                    Showing {start + 1}–{end} of {total} {isFetching ? "· refreshing…" : ""}
                  </>
                )}
              </div>
              <div className="pagination-controls">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={!hasPrev}
                >
                  ‹ Prev
                </Button>
                <span>
                  Page {page + 1} / {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPage(page + 1)}
                  disabled={!hasNext}
                >
                  Next ›
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function ExportControls({ filters }: { filters: LeadFilters }) {
  const { user } = useAuth();
  const { current } = useWorkspace();
  const [open, setOpen] = useState(false);

  // Server gates GET /api/exports/leads with requireRole("manager"). Hide the
  // control for sales-only members so it doesn't dangle as a 403 button.
  // Superadmins (acting on any workspace) and managers/admins keep it.
  if (user && !user.isSuperadmin && user.role === "sales") return null;

  return (
    <div className="header-actions">
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} disabled={!current}>
        Export CSV
      </Button>
      {open ? (
        <ExportLeadsModal
          workspaceSlug={current?.slug ?? null}
          filters={filters}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}
